/**
 * TerraIndex-export mét foto's: één zip met een map erin, met het exportbestand en een submap
 * 'fotos' met alle foto's van de gekozen boringen.
 *
 *   geovoice-project-2026-10-14.zip
 *   └── geovoice-project-2026-10-14/
 *       ├── geovoice-project-2026-10-14.xml
 *       └── fotos/
 *           ├── B55_01_laag-0-40-cm.jpg
 *           └── B55_02.jpg
 *
 * De foto's komen in één platte map, met de boringnaam vooraan zodat je ze in TerraIndex of de
 * verkenner bij de juiste boring terugvindt. De zip wordt in de browser gemaakt (fflate): de
 * foto's staan achter de API en de server hoeft er zo geen honderden MB voor in het geheugen te
 * houden.
 */
import { zipSync } from 'fflate';

const EXTENSIES = { 'image/jpeg': 'jpg', 'image/jpg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/heic': 'heic', 'image/heif': 'heif' };

/** Veilig voor een bestandsnaam op Windows, macOS en in een zip. */
export function veiligeNaam(tekst, reserve = 'zonder-naam') {
  const s = String(tekst ?? '')
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[–—]/g, '-')
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '');
  return s.slice(0, 80) || reserve;
}

/** Extensie uit het type, anders uit de oorspronkelijke bestandsnaam, anders jpg. */
export function extensieVan(foto) {
  const uitType = EXTENSIES[String(foto?.contentType || '').toLowerCase()];
  if (uitType) return uitType;
  const m = /\.([A-Za-z0-9]{2,5})$/.exec(String(foto?.filename || ''));
  return m ? m[1].toLowerCase() : 'jpg';
}

/**
 * Bestandsnaam van een foto in de map 'fotos': boring, volgnummer binnen de boring, en waar
 * de foto bij hoort (de laag of het monster) of anders de omschrijving.
 */
export function fotoBestandsnaam(boringNaam, volgnummer, foto) {
  const nr = String(volgnummer).padStart(2, '0');
  const extra = foto?.targetLabel || foto?.description || '';
  const deel = extra ? `_${veiligeNaam(extra).toLowerCase()}` : '';
  return `${veiligeNaam(boringNaam, 'boring')}_${nr}${deel}.${extensieVan(foto)}`;
}

/** Maakt een naam uniek binnen de map (B55_01.jpg, B55_01-2.jpg, …). */
function uniek(naam, gebruikt) {
  if (!gebruikt.has(naam)) { gebruikt.add(naam); return naam; }
  const punt = naam.lastIndexOf('.');
  for (let i = 2; ; i++) {
    const kandidaat = `${naam.slice(0, punt)}-${i}${naam.slice(punt)}`;
    if (!gebruikt.has(kandidaat)) { gebruikt.add(kandidaat); return kandidaat; }
  }
}

/**
 * Haal de foto's van de boringen op, een paar tegelijk.
 *
 * @param {{ id: string, naam: string }[]} boringen
 * @param {object} api                  { lijst(boringId) → foto[], blob(fotoId) → Blob }
 * @param {(klaar:number, totaal:number) => void} [opVoortgang]
 * @returns {Promise<{ fotos: { naam: string, bytes: Uint8Array }[], mislukt: number }>}
 */
export async function haalFotosOp(boringen, api, opVoortgang = () => {}, gelijktijdig = 4) {
  const lijsten = await Promise.all(boringen.map(async (b) => {
    try { return { boring: b, fotos: (await api.lijst(b.id)) || [] }; }
    catch { return { boring: b, fotos: [], mislukt: true }; }
  }));
  const taken = [];
  const gebruikt = new Set();
  let mislukt = lijsten.filter((l) => l.mislukt).length;
  for (const { boring, fotos } of lijsten) {
    fotos.forEach((foto, i) => taken.push({ foto, naam: uniek(fotoBestandsnaam(boring.naam, i + 1, foto), gebruikt) }));
  }
  const uit = new Array(taken.length);
  let klaar = 0;
  opVoortgang(0, taken.length);
  let volgende = 0;
  const werker = async () => {
    while (volgende < taken.length) {
      const i = volgende++;
      try {
        const blob = await api.blob(taken[i].foto.id);
        uit[i] = { naam: taken[i].naam, bytes: new Uint8Array(await blob.arrayBuffer()) };
      } catch {
        mislukt += 1;
      }
      opVoortgang(++klaar, taken.length);
    }
  };
  await Promise.all(Array.from({ length: Math.min(gelijktijdig, taken.length) }, werker));
  return { fotos: uit.filter(Boolean), mislukt };
}

/**
 * De zip zelf. Foto's ongecomprimeerd opgeslagen (ze zijn al gecomprimeerd; nog eens inpakken
 * kost alleen tijd), het exportbestand wel gecomprimeerd.
 */
export function bouwExportZip({ map, exportNaam, exportBytes, fotos }) {
  const bestanden = {
    [`${map}/${exportNaam}`]: [exportBytes, { level: 6 }],
    [`${map}/fotos/`]: new Uint8Array(0),
  };
  for (const f of fotos) bestanden[`${map}/fotos/${f.naam}`] = [f.bytes, { level: 0 }];
  return zipSync(bestanden);
}
